import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { FolderTabs } from '../../../../components/FolderTabs';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { TextField } from '../../../../components/inputs/TextField';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdDate, TdInput, TdMoney, TdNumber } from '../../../../components/inline-add-table/cells';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import type { PersonnelSaln } from '../personnelTypes';
import { sampleSaln } from '../utils/sampleSaln';

/*
  ── the four schedules ────────────────────────────────────────────────────────────────

  Each draft row carries a client-only `key` for React and for the cell ids. It is NOT sent:
  a correction replaces the schedule wholesale, so the server flags the old lines and writes
  these, and a client-supplied id would name a row the client does not own.

  The SortOrder is the array order, which the server numbers. Nothing here sends a position.
*/

type ScheduleKey =
  | 'realProperties'
  | 'personalProperties'
  | 'liabilities'
  | 'businessInterests';

interface RealRow {
  key: string;
  description: string;
  exactLocation: string;
  assessedValue: number | null;
  fairMarketValue: number | null;
  acquisitionYear: number | null;
  acquisitionMode: string;
  acquisitionCost: number | null;
}

interface PersonalRow {
  key: string;
  description: string;
  yearAcquired: number | null;
  acquisitionCost: number | null;
}

interface LiabilityRow {
  key: string;
  nature: string;
  creditorName: string;
  outstandingBalance: number | null;
}

interface InterestRow {
  key: string;
  entityName: string;
  businessAddress: string;
  natureOfInterest: string;
  acquisitionDate: string;
}

const EMPTY_REAL = (): RealRow => ({
  key: uid(),
  description: '',
  exactLocation: '',
  assessedValue: null,
  fairMarketValue: null,
  acquisitionYear: null,
  acquisitionMode: '',
  acquisitionCost: null,
});

const EMPTY_PERSONAL = (): PersonalRow => ({
  key: uid(),
  description: '',
  yearAcquired: null,
  acquisitionCost: null,
});

const EMPTY_LIABILITY = (): LiabilityRow => ({
  key: uid(),
  nature: '',
  creditorName: '',
  outstandingBalance: null,
});

const EMPTY_INTEREST = (): InterestRow => ({
  key: uid(),
  entityName: '',
  businessAddress: '',
  natureOfInterest: '',
  acquisitionDate: '',
});

/** The earliest year the server will accept. Mirrors `PersonnelFieldRules.MinSalnYear`. */
const MIN_YEAR = 1900;

/**
 * `sortOrder` → `sort-order`, exactly as `focusValidationError` does it.
 *
 * Copied rather than imported because that one is private to the helper — and it has to STAY
 * identical: it is the only thing making `cellId` and the server's field names meet.
 */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * The id of one inline cell — `saln-real-properties.2.acquisition-cost`.
 *
 * **This is a contract with the server.** `PersonnelService.SalnLine` rejects a schedule line as
 * `realProperties.2.acquisitionCost`, and `focusValidationError` resolves that to
 * `${idPrefix}-${kebab(field)}`. Change either side and the reader is told which cell is wrong
 * while nothing on screen moves — which reads as the feature not being wired at all.
 */
const cellId = (schedule: ScheduleKey, index: number, column: string) =>
  `saln-${kebab(schedule)}.${index}.${kebab(column)}`;

/** The folder a rejected field belongs to, so the strip can switch before the focus lands. */
const FOLDER_OF: Record<ScheduleKey, number> = {
  realProperties: 0,
  personalProperties: 1,
  liabilities: 2,
  businessInterests: 3,
};

/**
 * Is this line filled in enough to count?
 *
 * The required column and nothing else, which is the definition reference §12 sets: "a row is
 * complete when its `*` required column has a value". It is the one predicate behind BOTH of
 * that section's two rules — the `+` that locks while a draft is incomplete, and the badge that
 * counts only valid rows — so the two cannot come to disagree about what a blank row is.
 */
const filled = (value: string) => value.trim().length > 0;

/** Sums a money column over the DRAFT rows. An absent figure adds nothing. */
const sum = <T,>(rows: T[], pick: (row: T) => number | null) =>
  rows.reduce((total, row) => total + (pick(row) ?? 0), 0);

/**
 * Records or corrects one SALN filing — the annual disclosure under RA 6713.
 *
 * **Captured from the hospital system's own Add SALN form on 17 Sep 2026**, and it keeps that
 * form's shape: the filing's own facts at the top, then the four schedules as folders, each an
 * inline-add table with its total below it.
 *
 * **THE FOUR SCHEDULES ARE FOLDERS, and each one owns its total.** `<FolderTabs>` over one panel
 * — the standard horizontal folder nav, one level deep — because a SALN has four independent
 * tables and stacking them makes a form nobody can see the shape of. The tab carries a count so
 * a reader knows which folders hold anything before opening them.
 *
 * **The total under each table is a `<PreviewTable variant="table-display">`.** It is a read-only
 * fact being shown rather than a value being asked for, which is the test that separates the two
 * treatments — and it sits BELOW the table because it is what the table adds up to.
 *
 * **These totals are the DRAFT's arithmetic, and that is not the second-sum failure.** The record
 * has no server total until it is saved, so the folder has to add up the rows on screen; one
 * `sum` here feeds every one of them, so no two folders can disagree. The moment the filing IS
 * saved, the panel reads the SERVER's derived totals and this arithmetic is not consulted again.
 * **The filing-level net worth is deliberately absent here** — it is the server's, and it reads
 * on the panel.
 *
 * **Connections counts toward no total**, so its folder shows a count where the other three show
 * money. That is the form's shape rather than an omission: an interest is a connection to be
 * disclosed, not an amount.
 *
 * **A blank row is dropped, never rejected.** An inline-add table hands back whatever is on
 * screen, and a reader who pressed `+` and changed their mind left an empty row — refusing the
 * save over it would be a rejection they did not cause. A row with a value in it and no
 * description IS refused, because that one is half-filled rather than abandoned.
 */
export function EditSalnModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelSaln | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const thisYear = new Date().getFullYear();

  const [asOfYear, setAsOfYear] = useState(
    /*
      A NEW filing defaults to LAST year, not this one. A SALN is filed for a year that has
      finished, so the year a reader almost always means is the one just gone — and the server
      refuses a year in the future, so defaulting forward would open the form already wrong.
    */
    String(editing?.asOfYear ?? thisYear - 1),
  );

  const [isJointFiling, setIsJointFiling] = useState(editing?.isJointFiling ?? false);
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  const [realRows, setRealRows] = useState<RealRow[]>(() =>
    (editing?.realProperties ?? []).map((x) => ({
      key: uid(),
      description: x.description,
      exactLocation: x.exactLocation ?? '',
      assessedValue: x.assessedValue ?? null,
      fairMarketValue: x.fairMarketValue ?? null,
      acquisitionYear: x.acquisitionYear ?? null,
      acquisitionMode: x.acquisitionMode ?? '',
      acquisitionCost: x.acquisitionCost ?? null,
    })),
  );

  const [personalRows, setPersonalRows] = useState<PersonalRow[]>(() =>
    (editing?.personalProperties ?? []).map((x) => ({
      key: uid(),
      description: x.description,
      yearAcquired: x.yearAcquired ?? null,
      acquisitionCost: x.acquisitionCost ?? null,
    })),
  );

  const [liabilityRows, setLiabilityRows] = useState<LiabilityRow[]>(() =>
    (editing?.liabilities ?? []).map((x) => ({
      key: uid(),
      nature: x.nature,
      creditorName: x.creditorName ?? '',
      outstandingBalance: x.outstandingBalance ?? null,
    })),
  );

  const [interestRows, setInterestRows] = useState<InterestRow[]>(() =>
    (editing?.businessInterests ?? []).map((x) => ({
      key: uid(),
      entityName: x.entityName,
      businessAddress: x.businessAddress ?? '',
      natureOfInterest: x.natureOfInterest ?? '',
      acquisitionDate: x.acquisitionDate ?? '',
    })),
  );

  const [folder, setFolder] = useState<ScheduleKey>('realProperties');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const year = Number.parseInt(asOfYear, 10);
  const yearValid = Number.isInteger(year) && year >= MIN_YEAR && year <= thisYear;

  const canSave = yearValid && confirmed && !saving;

  /*
    The draft totals. ONE `sum`, four call sites — the tab's own folder and the preview under it
    read the same number by construction, which is what stops a tab and its table disagreeing.
  */
  const realTotal = sum(realRows, (x) => x.acquisitionCost);
  const personalTotal = sum(personalRows, (x) => x.acquisitionCost);
  const liabilityTotal = sum(liabilityRows, (x) => x.outstandingBalance);

  /*
    REFERENCE §12's two rules, and they ship together because they are one predicate read twice.

    The `+` LOCKS while the draft row at the bottom is incomplete, so a misclick cannot spawn a
    second blank row — the button's own tooltip says why. And a tab BADGE counts only the lines
    that are filled in, never `rows.length`: a blank draft must contribute 0, or the strip
    reports a schedule as holding something the save is about to drop.

    Counted over EVERY row rather than the last one, because a reader can empty a row in the
    middle — the schedule is incomplete either way, and locking on the last row alone would let
    a second blank one be added above it.
  */
  const realValid = realRows.filter((x) => filled(x.description)).length;
  const personalValid = personalRows.filter((x) => filled(x.description)).length;
  const liabilityValid = liabilityRows.filter((x) => filled(x.nature)).length;
  const interestValid = interestRows.filter((x) => filled(x.entityName)).length;

  /** Fills every required field, and the optional ones the form can offer a real value for. */
  function generate() {
    const sample = sampleSaln();

    setAsOfYear(String(sample.asOfYear));
    setRemarks(sample.remarks);

    setRealRows(sample.realProperties.map((x) => ({ key: uid(), ...x })));
    setPersonalRows(sample.personalProperties.map((x) => ({ key: uid(), ...x })));
    setLiabilityRows(sample.liabilities.map((x) => ({ key: uid(), ...x })));
    setInterestRows(sample.businessInterests.map((x) => ({ key: uid(), ...x })));
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    /*
      EVERY row is sent, blanks included, so the INDEX the server names on a rejection is the
      index of the row on screen. Dropping the blanks here would shift every later row's number
      and point the reader at the wrong cell; the server drops them instead, after validating.
    */
    const request = {
      asOfYear: year,
      isJointFiling,

      // Empty is ABSENT, not a blank value — one meaning for the null downstream.
      remarks: remarks.trim() || null,

      realProperties: realRows.map((x) => ({
        description: x.description.trim(),
        exactLocation: x.exactLocation.trim() || null,
        assessedValue: x.assessedValue,
        fairMarketValue: x.fairMarketValue,
        acquisitionYear: x.acquisitionYear,
        acquisitionMode: x.acquisitionMode.trim() || null,
        acquisitionCost: x.acquisitionCost,
      })),

      personalProperties: personalRows.map((x) => ({
        description: x.description.trim(),
        yearAcquired: x.yearAcquired,
        acquisitionCost: x.acquisitionCost,
      })),

      liabilities: liabilityRows.map((x) => ({
        nature: x.nature.trim(),
        creditorName: x.creditorName.trim() || null,
        outstandingBalance: x.outstandingBalance,
      })),

      businessInterests: interestRows.map((x) => ({
        entityName: x.entityName.trim(),
        businessAddress: x.businessAddress.trim() || null,
        natureOfInterest: x.natureOfInterest.trim() || null,
        acquisitionDate: x.acquisitionDate || null,
      })),
    };

    try {
      if (editing) await personnelApi.updateSaln(personnelId, editing.id, request);
      else await personnelApi.addSaln(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this filing.', 0);

      setError(failure);

      /*
        A rejected CELL is not in the DOM until its folder is open, which is the same problem a
        stepped form has — so `stepOf` / `onStep` are reused for the folder strip. The names say
        "step" and the mechanism is "switch to whatever holds this field, then focus it", which
        is exactly what a folder needs.
      */
      focusValidationError(failure, {
        idPrefix: 'saln',
        fieldIds: {
          // The checkbox's own id, which `kebab` would otherwise miss entirely.
          isJointFiling: 'saln-is-joint-filing',
        },
        stepOf: (field) => {
          const schedule = field.split('.')[0] as ScheduleKey;
          return FOLDER_OF[schedule];
        },
        onStep: (index) => {
          const key = (Object.keys(FOLDER_OF) as ScheduleKey[])[index];
          if (key) setFolder(key);
        },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit SALN filing' : 'Add a SALN filing'}
      size="3xl"
      id="saln-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'saln-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="saln-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="saln-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The filing's OWN facts, above the strip and visible from every folder. They identify
          the filing rather than belonging to one schedule, so a fifth tab for them would ask the
          reader to leave the table they are filling in to check which year they are filling it
          for.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="saln-as-of-year"
            label="As of year"
            required
            type="number"
            // A year is a machine value, so it takes the mono face — through className, which is
            // what <TextField> exposes; there is no `mono` flag on it as there is on a cell.
            className="font-mono tabular-nums"
            description="The calendar year the declaration is as of. A SALN is filed for a year that has finished, so this is never a future year."
            value={asOfYear}
            onChange={(e) => setAsOfYear(e.target.value)}
            error={
              yearValid
                ? error?.fieldError('asOfYear')
                : `Enter a year between ${MIN_YEAR} and ${thisYear}.`
            }
            disabled={saving}
          />

          <div className="flex items-end pb-2">
            <label
              htmlFor="saln-is-joint-filing"
              className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
            >
              <Checkbox
                id="saln-is-joint-filing"
                name="saln-is-joint-filing"
                checked={isJointFiling}
                onChange={(e) => setIsJointFiling(e.target.checked)}
                disabled={saving}
              />
              <span>Filed jointly with a spouse</span>
            </label>
          </div>
        </div>

        <TextField
          id="saln-remarks"
          label="Remarks"
          description="Anything else worth saying about this filing. Optional."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />

        {/*
          THE FOUR SCHEDULES. `<FolderTabs>` over one panel — the standard horizontal folder nav,
          one level deep, never a hand-rolled strip and never an underlined one. The counts live
          inside the label through `<CountBadge>`, and a zero renders: that is what tells a reader
          which folders are empty before they open them.
        */}
        {/*
          THE STRIP SITS ON SLATE-100, and the band is not decoration. The three surfaces have to
          read front to back — white active tab, slate-50 inactive tabs, slate-100 behind them —
          and a modal body is WHITE, which is lighter than the inactive tabs. Without the band the
          stack inverts and every unselected tab reads as sitting in front of nothing.

          One container pads the strip AND the panel, because the two must share a left edge or
          the active tab's merge lands over the panel's curve instead of its straight top line.
        */}
        <div className="rounded-panel bg-slate-100 px-3 pb-3 pt-2">
          <FolderTabs<ScheduleKey>
            id="saln-folders"
            ariaLabel="SALN schedules"
            paddingX="px-3"
            activeKey={folder}
            onChange={setFolder}
            tabs={[
              {
                key: 'realProperties',
                label: (
                  <>
                    Real properties <CountBadge count={realValid} />
                  </>
                ),
              },
              {
                key: 'personalProperties',
                label: (
                  <>
                    Personal properties <CountBadge count={personalValid} />
                  </>
                ),
              },
              {
                key: 'liabilities',
                label: (
                  <>
                    Liabilities <CountBadge count={liabilityValid} />
                  </>
                ),
              },
              {
                key: 'businessInterests',
                label: (
                  <>
                    Connections <CountBadge count={interestValid} />
                  </>
                ),
              },
            ]}
          />

          <div className="rounded-b-panel border border-t-0 border-slate-300 bg-white p-3">
            {folder === 'realProperties' ? (
              <div className="flex flex-col gap-3">
                <InlineAddTable
                  headers={[
                    'Description *',
                    'Location',
                    'Acq. year',
                    'Mode',
                    'Assessed',
                    'Market',
                    'Acq. cost',
                  ]}
                  colWidths={[220, 200, 90, 130, 130, 130, 140]}
                  resizeKey="saln-real.colWidths.v1"
                  flexColumn={0}
                  addButtonId="saln-real-add"
                  addDisabled={realValid < realRows.length}
                  rowCount={realRows.length}
                  onAddRow={() => setRealRows((rows) => [...rows, EMPTY_REAL()])}
                >
                  {realRows.map((row, index) => (
                    <InlineRow
                      key={row.key}
                      idx={index + 1}
                      onRemove={() =>
                        setRealRows((rows) => rows.filter((x) => x.key !== row.key))
                      }
                    >
                      <TdInput
                        id={cellId('realProperties', index, 'description')}
                        ariaLabel={`Description, row ${index + 1}`}
                        value={row.description}
                        disabled={saving}
                        onChange={(next) =>
                          setRealRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, description: next } : x)),
                          )
                        }
                      />
                      <TdInput
                        id={cellId('realProperties', index, 'exactLocation')}
                        ariaLabel={`Location, row ${index + 1}`}
                        value={row.exactLocation}
                        disabled={saving}
                        onChange={(next) =>
                          setRealRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, exactLocation: next } : x)),
                          )
                        }
                      />
                      <TdNumber
                        id={cellId('realProperties', index, 'acquisitionYear')}
                        ariaLabel={`Year acquired, row ${index + 1}`}
                        value={row.acquisitionYear}
                        allowNull
                        integerOnly
                        min={MIN_YEAR}
                        max={thisYear}
                        disabled={saving}
                        onChange={(next) =>
                          setRealRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, acquisitionYear: next } : x,
                            ),
                          )
                        }
                      />
                      <TdInput
                        id={cellId('realProperties', index, 'acquisitionMode')}
                        ariaLabel={`Mode of acquisition, row ${index + 1}`}
                        placeholder="Purchase"
                        value={row.acquisitionMode}
                        disabled={saving}
                        onChange={(next) =>
                          setRealRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, acquisitionMode: next } : x,
                            ),
                          )
                        }
                      />
                      <TdMoney
                        id={cellId('realProperties', index, 'assessedValue')}
                        ariaLabel={`Assessed value, row ${index + 1}`}
                        value={row.assessedValue}
                        disabled={saving}
                        onChange={(next) =>
                          setRealRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, assessedValue: next } : x)),
                          )
                        }
                      />
                      <TdMoney
                        id={cellId('realProperties', index, 'fairMarketValue')}
                        ariaLabel={`Fair market value, row ${index + 1}`}
                        value={row.fairMarketValue}
                        disabled={saving}
                        onChange={(next) =>
                          setRealRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, fairMarketValue: next } : x,
                            ),
                          )
                        }
                      />
                      <TdMoney
                        id={cellId('realProperties', index, 'acquisitionCost')}
                        ariaLabel={`Acquisition cost, row ${index + 1}`}
                        value={row.acquisitionCost}
                        disabled={saving}
                        onChange={(next) =>
                          setRealRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, acquisitionCost: next } : x,
                            ),
                          )
                        }
                      />
                    </InlineRow>
                  ))}
                </InlineAddTable>

                {/*
                  THE TOTAL, BELOW THE TABLE, as a Table Display View — a read-only fact being
                  SHOWN rather than a value being asked for, which is the test separating the two
                  treatments. It sums the ACQUISITION COST and not the market value, because that
                  is what a SALN totals: summing a valuation nobody declared would make the net
                  worth a number the filer never stated.
                */}
                <PreviewTable
                  variant="table-display"
                  rows={[
                    {
                      label: 'Total acquisition cost',
                      value: formatMoney(realTotal),
                      mono: true,
                    },
                  ]}
                />
              </div>
            ) : null}

            {folder === 'personalProperties' ? (
              <div className="flex flex-col gap-3">
                <InlineAddTable
                  headers={['Description *', 'Year acquired', 'Acquisition cost']}
                  colWidths={[360, 140, 180]}
                  resizeKey="saln-personal.colWidths.v1"
                  flexColumn={0}
                  addButtonId="saln-personal-add"
                  addDisabled={personalValid < personalRows.length}
                  rowCount={personalRows.length}
                  onAddRow={() => setPersonalRows((rows) => [...rows, EMPTY_PERSONAL()])}
                >
                  {personalRows.map((row, index) => (
                    <InlineRow
                      key={row.key}
                      idx={index + 1}
                      onRemove={() =>
                        setPersonalRows((rows) => rows.filter((x) => x.key !== row.key))
                      }
                    >
                      <TdInput
                        id={cellId('personalProperties', index, 'description')}
                        ariaLabel={`Description, row ${index + 1}`}
                        value={row.description}
                        disabled={saving}
                        onChange={(next) =>
                          setPersonalRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, description: next } : x)),
                          )
                        }
                      />
                      <TdNumber
                        id={cellId('personalProperties', index, 'yearAcquired')}
                        ariaLabel={`Year acquired, row ${index + 1}`}
                        value={row.yearAcquired}
                        allowNull
                        integerOnly
                        min={MIN_YEAR}
                        max={thisYear}
                        disabled={saving}
                        onChange={(next) =>
                          setPersonalRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, yearAcquired: next } : x)),
                          )
                        }
                      />
                      <TdMoney
                        id={cellId('personalProperties', index, 'acquisitionCost')}
                        ariaLabel={`Acquisition cost, row ${index + 1}`}
                        value={row.acquisitionCost}
                        disabled={saving}
                        onChange={(next) =>
                          setPersonalRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, acquisitionCost: next } : x,
                            ),
                          )
                        }
                      />
                    </InlineRow>
                  ))}
                </InlineAddTable>

                <PreviewTable
                  variant="table-display"
                  rows={[
                    {
                      label: 'Total acquisition cost',
                      value: formatMoney(personalTotal),
                      mono: true,
                    },
                  ]}
                />
              </div>
            ) : null}

            {folder === 'liabilities' ? (
              <div className="flex flex-col gap-3">
                <InlineAddTable
                  headers={['Nature *', 'Creditor', 'Outstanding balance']}
                  colWidths={[300, 240, 180]}
                  resizeKey="saln-liabilities.colWidths.v1"
                  flexColumn={0}
                  addButtonId="saln-liabilities-add"
                  addDisabled={liabilityValid < liabilityRows.length}
                  rowCount={liabilityRows.length}
                  onAddRow={() => setLiabilityRows((rows) => [...rows, EMPTY_LIABILITY()])}
                >
                  {liabilityRows.map((row, index) => (
                    <InlineRow
                      key={row.key}
                      idx={index + 1}
                      onRemove={() =>
                        setLiabilityRows((rows) => rows.filter((x) => x.key !== row.key))
                      }
                    >
                      <TdInput
                        id={cellId('liabilities', index, 'nature')}
                        ariaLabel={`Nature, row ${index + 1}`}
                        placeholder="Housing loan"
                        value={row.nature}
                        disabled={saving}
                        onChange={(next) =>
                          setLiabilityRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, nature: next } : x)),
                          )
                        }
                      />
                      <TdInput
                        id={cellId('liabilities', index, 'creditorName')}
                        ariaLabel={`Creditor, row ${index + 1}`}
                        value={row.creditorName}
                        disabled={saving}
                        onChange={(next) =>
                          setLiabilityRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, creditorName: next } : x)),
                          )
                        }
                      />
                      <TdMoney
                        id={cellId('liabilities', index, 'outstandingBalance')}
                        ariaLabel={`Outstanding balance, row ${index + 1}`}
                        value={row.outstandingBalance}
                        disabled={saving}
                        onChange={(next) =>
                          setLiabilityRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, outstandingBalance: next } : x,
                            ),
                          )
                        }
                      />
                    </InlineRow>
                  ))}
                </InlineAddTable>

                <PreviewTable
                  variant="table-display"
                  rows={[
                    {
                      label: 'Total outstanding balance',
                      value: formatMoney(liabilityTotal),
                      mono: true,
                    },
                  ]}
                />
              </div>
            ) : null}

            {folder === 'businessInterests' ? (
              <div className="flex flex-col gap-3">
                <InlineAddTable
                  headers={['Entity / business *', 'Address', 'Nature of interest', 'Date acquired']}
                  colWidths={[240, 220, 200, 160]}
                  resizeKey="saln-business.colWidths.v1"
                  flexColumn={0}
                  addButtonId="saln-business-add"
                  addDisabled={interestValid < interestRows.length}
                  rowCount={interestRows.length}
                  onAddRow={() => setInterestRows((rows) => [...rows, EMPTY_INTEREST()])}
                >
                  {interestRows.map((row, index) => (
                    <InlineRow
                      key={row.key}
                      idx={index + 1}
                      onRemove={() =>
                        setInterestRows((rows) => rows.filter((x) => x.key !== row.key))
                      }
                    >
                      <TdInput
                        id={cellId('businessInterests', index, 'entityName')}
                        ariaLabel={`Entity or business, row ${index + 1}`}
                        value={row.entityName}
                        disabled={saving}
                        onChange={(next) =>
                          setInterestRows((rows) =>
                            rows.map((x) => (x.key === row.key ? { ...x, entityName: next } : x)),
                          )
                        }
                      />
                      <TdInput
                        id={cellId('businessInterests', index, 'businessAddress')}
                        ariaLabel={`Address, row ${index + 1}`}
                        value={row.businessAddress}
                        disabled={saving}
                        onChange={(next) =>
                          setInterestRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, businessAddress: next } : x,
                            ),
                          )
                        }
                      />
                      <TdInput
                        id={cellId('businessInterests', index, 'natureOfInterest')}
                        ariaLabel={`Nature of interest, row ${index + 1}`}
                        placeholder="Sole proprietor"
                        value={row.natureOfInterest}
                        disabled={saving}
                        onChange={(next) =>
                          setInterestRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, natureOfInterest: next } : x,
                            ),
                          )
                        }
                      />
                      {/*
                        A CALENDAR date, so the cell is a plain `type="date"` and the value stays
                        `YYYY-MM-DD` from the input to the wire. Nothing here parses it into a
                        `Date` — that is the instant path, and it would move the day for a reader
                        west of UTC.
                      */}
                      <TdDate
                        id={cellId('businessInterests', index, 'acquisitionDate')}
                        ariaLabel={`Date acquired, row ${index + 1}`}
                        value={row.acquisitionDate}
                        disabled={saving}
                        onChange={(next) =>
                          setInterestRows((rows) =>
                            rows.map((x) =>
                              x.key === row.key ? { ...x, acquisitionDate: next } : x,
                            ),
                          )
                        }
                      />
                    </InlineRow>
                  ))}
                </InlineAddTable>

                {/*
                  A COUNT, not money. This schedule counts toward no total — an interest is a
                  connection to be disclosed rather than an amount — so the folder states what it
                  actually has, which is how many were declared. Rendering a currency total of
                  zero here would claim the schedule was worth nothing.
                */}
                <PreviewTable
                  variant="table-display"
                  rows={[
                    {
                      label: 'Connections declared',
                      value: String(interestRows.length),
                      mono: true,
                    },
                  ]}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
